import React, { useState, useEffect, useRef, Fragment } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, setDoc, addDoc, collection, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { defaultTemplate } from '../../utils/scenarioBuilder/defaultTemplates';
import { handleRowDragEnd, handleElementDragEnd } from '../../utils/scenarioBuilder/dragDropHandlers';
import { calculateSectionTotal, getAllSectionTotals, calculateIncludedRowsTotals } from '../../utils/scenarioBuilder/sectionTotals';
import { generatePDF } from '../../utils/scenarioBuilder/pdfGenerator';
import { useAuth } from '../../contexts/AuthContext';
import { isCustomModule, renderCustomModule } from './CustomModules';
import CustomModuleModal from './modals/CustomModuleModal';

// MacBook touchpad zoom detection and compensation
const useViewportZoom = () => {
  const [zoomLevel, setZoomLevel] = useState(1);
  
  useEffect(() => {
    const detectZoom = () => {
      // Detect zoom by comparing visual viewport to layout viewport
      const visualViewport = window.visualViewport;
      const layoutViewport = document.documentElement.clientWidth;
      
      if (visualViewport) {
        const zoom = layoutViewport / visualViewport.width;
        setZoomLevel(zoom);
      } else {
        // Fallback for browsers without visualViewport support
        const zoom = window.devicePixelRatio;
        setZoomLevel(zoom);
      }
    };
    
    // Initial detection
    detectZoom();
    
    // Listen for visual viewport changes (touchpad zoom)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', detectZoom);
      window.visualViewport.addEventListener('scroll', detectZoom);
    }
    
    // Fallback listeners
    window.addEventListener('resize', detectZoom);
    window.addEventListener('wheel', detectZoom, { passive: true });
    
    // Cleanup
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', detectZoom);
        window.visualViewport.removeEventListener('scroll', detectZoom);
      }
      window.removeEventListener('resize', detectZoom);
      window.removeEventListener('wheel', detectZoom);
    };
  }, []);
  
  return zoomLevel;
};

// Zoom-compensation styles for MacBook touchpad zoom
const sidebarStyles = `
  .scenario-sidebar {
    position: fixed !important;
    left: 0 !important;
    top: 4rem !important;
    width: 16rem !important;
    height: calc(100vh - 4rem) !important;
    min-width: 16rem !important;
    max-width: 16rem !important;
    box-sizing: border-box !important;
    z-index: 40 !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    transform-origin: top left !important;
  }
  
  .scenario-main-content {
    margin-left: 16rem !important;
    min-width: calc(100vw - 16rem) !important;
    box-sizing: border-box !important;
    width: calc(100vw - 16rem) !important;
  }
  
  /* Ensure sidebar content doesn't overflow when zoomed */
  .scenario-sidebar .sidebar-content {
    padding: 1rem;
    width: 100%;
    box-sizing: border-box;
  }
  
  .scenario-sidebar .sidebar-button {
    width: 100% !important;
    box-sizing: border-box !important;
    font-size: 0.875rem !important;
    padding: 0.75rem 1rem !important;
    word-break: break-word !important;
  }
  
  @media (max-width: 768px) {
    .scenario-sidebar {
      width: 12rem !important;
      min-width: 12rem !important;
      max-width: 12rem !important;
    }
    
    .scenario-main-content {
      margin-left: 12rem !important;
      min-width: calc(100vw - 12rem) !important;
      width: calc(100vw - 12rem) !important;
    }
  }
  
  /* Prevent table distortion during drag operations */
  .react-beautiful-dnd-dragging {
    table-layout: fixed !important;
  }
  
  .react-beautiful-dnd-dragging td {
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }
  
  /* Maintain table structure during drag */
  tbody.react-beautiful-dnd-droppable {
    table-layout: fixed !important;
  }
  
  table {
    table-layout: fixed !important;
  }
  
  /* Fix for drag handle column width during drag */
  .react-beautiful-dnd-dragging td:first-child {
    width: 32px !important;
    min-width: 32px !important;
    max-width: 32px !important;
  }
  
  /* Fix for row number column width during drag */
  .react-beautiful-dnd-dragging td:nth-child(2) {
    width: 32px !important;
    min-width: 32px !important;
    max-width: 32px !important;
  }
`;

// ====================================================================
// SCENARIO EDITOR STYLING GUIDE & CUSTOMIZATION REFERENCE
// ====================================================================
// 
// 🎨 MAIN UI COMPONENTS & THEIR STYLING:
// 
// 1. HEADER SECTIONS (Info Panels):
//    Location: Lines 660-780 (Scenario Info & Dispatch Info)
//    Current: gap-4 p-4 border border-gray-200 rounded-lg
//    Customize: Container backgrounds, borders, spacing
//
// 2. TABLE CONTROLS (Top Bar):
//    Location: Lines 780-870 (Column toggles)
//    Current: Button colors bg-gray-100, dropdown menus
//    Customize: Button colors, control layout, spacing
//
// 3. TABLE HEADERS:
//    Location: Lines 890-950 (Column headers with resize handles)
//    Current: bg-gray-200 text-gray-800 border-b-2 border-gray-300
//    Customize: Header backgrounds, text colors, border styles
//
// 4. ROW TYPES:
//    Section Headers: bg-indigo-50 border-indigo-200 (Lines 970-1020)
//    Sub-Headers: bg-purple-50 border-purple-200 (Lines 1020-1100)
//    Regular Rows: Alternating backgrounds (Lines 1200+)
//    Custom Modules: Specialized per-module styling
//
// 5. EDIT MODALS:
//    HeaderEditor: border-blue-500 bg-white (Form field editing)
//    CellEditor: border-blue-500 bg-white (Table cell editing)
//    Buttons: bg-green-500 (confirm), bg-red-500 (cancel)
//
// 6. ACTION BUTTONS:
//    Location: Lines 1300+ (Add Row, Header, etc.)
//    Current: bg-blue-600 (rows), bg-emerald-600 (headers), bg-indigo-600 (sub-headers)
//    Customize: Button colors, hover effects, shadows
//
// 7. SECTION HEADER TEXT:
//    Style: font-bold text-lg text-center (centered text in headers)
//    Location: EditableCell component, isHeader styling
//    Customize: Font size, weight, alignment, colors
//
// 🛠️ KEY TAILWIND CLASSES TO MODIFY:
// 
// Colors:
// - Primary buttons: bg-blue-600 hover:bg-blue-700
// - Section headers: bg-indigo-50 border-indigo-200 text-indigo-600
// - Sub-headers: bg-purple-50 border-purple-200 text-purple-600
// - Success actions: bg-green-500 hover:bg-green-600
// - Delete actions: text-red-500 hover:text-red-700
//
// Spacing:
// - Container padding: p-4 (16px)
// - Row gaps: gap-3 my-1 (12px gap, 4px margin)
// - Button padding: px-4 py-2 (standard button size)
// - Cell padding: (table cells)
//
// Typography:
// - Headers: font-bold text-lg (section headers)
// - Labels: font-semibold text-gray-700 text-sm
// - Body text: text-sm leading-relaxed
// - Small text: text-xs leading-snug
//
// 📝 TO CUSTOMIZE SPECIFIC ELEMENTS:
// 1. Find the component/section in the comments above
// 2. Locate the className attribute
// 3. Modify the Tailwind classes as needed
// 4. Test in both light/dark themes if applicable
//
// ✅ TAILWIND CONVERSION COMPLETE: All legacy CSS classes have been converted to Tailwind utilities.
// The old CSS classes are documented in index.css for reference but can be removed.
// ====================================================================

// ====================================================================
// SCENARIO EDITOR STYLING GUIDE
// ====================================================================
// This file contains the main Scenario Editor UI components.
// All styling uses Tailwind CSS classes. Key areas for customization:
//
// 1. EDITOR MODALS (HeaderEditor, CellEditor):
//    - Edit boxes that appear when clicking on cells
//    - Colors: border-blue-500 (edit mode border)
//    - Buttons: bg-green-500 (confirm), bg-red-500 (cancel)
//
// 2. TABLE CELLS (EditableCell):
//    - Display and editing states for table content
//    - Headers: font-bold text-lg text-center (section headers)
//    - Regular cells: hover:bg-gray-50 (hover effect)
//
// 3. SCENARIO INFO CONTAINERS:
//    - Two-column layout with form fields
//    - All styling now uses Tailwind utility classes for easy customization
//
// 4. TABLE HEADERS:
//    - Column headers with resize handles
//    - Background: bg-gray-200, borders: border-gray-300
//
// 5. ROW TYPES:
//    - Headers: bg-indigo-50 (section headers)
//    - Sub-headers: bg-purple-50 (sub-sections)
//    - Regular rows: alternating backgrounds
//    - Custom modules: specialized styling per module
//
// 6. ACTION BUTTONS:
//    - Add buttons: bg-blue-600, bg-emerald-600, etc.
//    - Control buttons: bg-gray-100 (column toggles)
//    - Delete buttons: text-red-500 hover:text-red-700
//
// 7. PREVIEW CONTROLS:
//    - Mode switchers: bg-green-500 (active), text-gray-700 (inactive)
//    - Orientation buttons: bg-blue-500 (active)
//
// To customize colors, fonts, spacing, etc., modify the Tailwind classes
// in the className attributes throughout this file.
// ====================================================================

// Header Editor Component - for scenario name, scenario category, description
// STYLING: Edit modal that appears when clicking on form fields
// CUSTOMIZE: Border color (border-blue-500), button colors, padding
const HeaderEditor = ({ value, onChange, onConfirm, onCancel, isTextarea = false, placeholder = "Enter text..." }) => (
  <div className="flex items-center justify-center gap-2 bg-white border-2 border-blue-500 rounded-lg shadow-lg p-0">
    {isTextarea ? (
      <textarea 
        className="flex-1 border-none rounded-md resize-none focus:outline-none focus:ring-0 text-sm whitespace-pre-wrap break-words p-0" 
        value={value} 
        onChange={e => onChange(e.target.value)} 
        autoFocus 
        placeholder={placeholder}
        rows={3}
        style={{ minHeight: '20px', wordWrap: 'break-word', overflowWrap: 'break-word' }}
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onConfirm();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
      />
    ) : (
      <input 
        type="text"
        className="flex-1 border-none rounded-md focus:outline-none focus:ring-0 text-sm p-0" 
        value={value} 
        onChange={e => onChange(e.target.value)} 
        autoFocus 
        placeholder={placeholder}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onConfirm();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
      />
    )}
    <div className="flex gap-1">
      <button
        onClick={onConfirm}
        className="bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors p-2 w-8 h-8 flex items-center justify-center"
        title="Confirm"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      </button>
      <button
        onClick={onCancel}
        className="bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors p-2 w-8 h-8 flex items-center justify-center"
        title="Cancel"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
  </div>
);

// Cell Editor Component - for table cells
// STYLING: Edit modal for table cell content (items, objectives, notes, scores)
// CUSTOMIZE: Background (bg-white), border (border-blue-500), button sizes
const CellEditor = ({ value, onChange, onConfirm, onCancel, field }) => {
  const isScoreOrValue = field === 'score' || field === 'value';
  const isItem = field === 'item';

  return (
    <div className={`flex ${isScoreOrValue ? 'flex-col items-center' : 'items-start'} p-1 bg-white border-2 border-blue-500 rounded-lg shadow-lg`}>
      <textarea 
        className={`flex-1 w-full border-none rounded-md resize-none focus:outline-none focus:ring-0 text-sm whitespace-pre-wrap break-words px-1 ${isScoreOrValue ? 'text-center' : ''} ${isItem ? 'text-left' : ''}`} 
        value={value} 
        onChange={e => onChange(e.target.value)} 
        autoFocus 
        rows={1}
        style={{ minHeight: '12px', wordWrap: 'break-word', overflowWrap: 'break-word' }}
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onConfirm();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
      />
      <div className="flex gap-1">
        <button
          onClick={onConfirm}
          className="bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors p-1 w-6 h-6 flex items-center justify-center"
          title="Confirm"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </button>
        <button
          onClick={onCancel}
          className="bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors p-1 w-6 h-6 flex items-center justify-center"
          title="Cancel"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Editable Cell Component - for table cells with display and editing states
// STYLING: Main table cell display - handles both regular cells and section headers
// CUSTOMIZE: 
//   - Section headers: font-bold text-lg text-center (currently centered)
//   - Regular cells: hover:bg-gray-50, text-sm
//   - Score/Value cells: text-center alignment
//   - Height: min-h-8, padding: p-2
const EditableCell = ({ value, onChange, onConfirm, onCancel, isHeader, isSubHeader, isDescription, rowIndex, field, editingCell, setEditingCell, tempCellValue, setTempCellValue }) => {
  const isEditing = editingCell && editingCell.rowIndex === rowIndex && editingCell.field === field;

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleDivClick = () => {
    if (!editingCell) {
      setEditingCell({ rowIndex, field });
      setTempCellValue(value);
    }
  };

  if (isEditing) {
    if (isHeader || isDescription) {
      return (
        <HeaderEditor
          value={tempCellValue}
          onChange={setTempCellValue}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          isTextarea={isDescription} // Allow multi-line for descriptions, single-line for headers
          placeholder={isDescription ? "Enter description text..." : "Enter header text..."}
        />
      );
    }
    return (
      <CellEditor
        value={tempCellValue}
        onChange={setTempCellValue}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        field={field}
      />
    );
  }

  const isScoreOrValue = field === 'score' || field === 'value';
  const isItem = field === 'item';

  return (
    <div 
      className={`w-full text-sm cursor-pointer transition-all duration-150 hover:bg-gray-50 ${editingCell ? 'pointer-events-none opacity-60' : ''} ${isHeader ? `font-bold text-lg py-0 min-h-6 text-center flex items-center justify-center` : isDescription ? `italic text-lg py-3 px-4 min-h-8 text-center text-justify flex items-center justify-center` : `whitespace-pre-wrap break-words py-1`} ${isScoreOrValue ? 'text-center' : ''} ${isItem ? 'text-left' : ''}`}
      onClick={handleDivClick}
    >
      {value || <span className="text-gray-400 italic">{isHeader ? (isSubHeader ? 'Sub-Header' : 'Section Header') : isDescription ? 'Description...' : '...'}</span>}
    </div>
  );
};

const ScenarioEditor = ({ templateId, onSave }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  console.log('ScenarioEditor rendered with templateId:', templateId);

  // Template editing state
  const [template, setTemplate] = useState(defaultTemplate);
  const [editingField, setEditingField] = useState(null);
  const [tempValue, setTempValue] = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [tempCellValue, setTempCellValue] = useState('');
  const [showCustomModuleModal, setShowCustomModuleModal] = useState(false);
  
  // Use viewport zoom detection for MacBook touchpad zoom
  const zoomLevel = useViewportZoom();
  
  // Tailwind-based responsive width ratios
  const [itemWidthRatio, setItemWidthRatio] = useState(() => {
    const saved = localStorage.getItem('scenarioEditor-itemWidthRatio');
    return saved ? parseFloat(saved) : 0.3; // 30% of content area by default
  });
  
  // Calculate Tailwind width classes based on ratio
  const getItemWidthClass = () => {
    const percentage = Math.round(itemWidthRatio * 100);
    if (percentage <= 20) return 'w-1/5';
    if (percentage <= 25) return 'w-1/4';
    if (percentage <= 30) return 'w-[30%]';
    if (percentage <= 33) return 'w-1/3';
    if (percentage <= 40) return 'w-2/5';
    if (percentage <= 50) return 'w-1/2';
    if (percentage <= 60) return 'w-3/5';
    if (percentage <= 66) return 'w-2/3';
    if (percentage <= 75) return 'w-3/4';
    return 'w-4/5';
  };
  
  const getObjectiveWidthClass = () => {
    const percentage = Math.round((1 - itemWidthRatio) * 100);
    if (percentage <= 20) return 'w-1/5';
    if (percentage <= 25) return 'w-1/4';
    if (percentage <= 30) return 'w-[30%]';
    if (percentage <= 33) return 'w-1/3';
    if (percentage <= 40) return 'w-2/5';
    if (percentage <= 50) return 'w-1/2';
    if (percentage <= 60) return 'w-3/5';
    if (percentage <= 66) return 'w-2/3';
    if (percentage <= 75) return 'w-3/4';
    return 'w-4/5';
  };

  // Column visibility state (Critical column is always visible)
  const [visibleColumns, setVisibleColumns] = useState(() => {
    // Start with localStorage as fallback
    const saved = localStorage.getItem('scenarioEditor-visibleColumns');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        console.log('Loaded saved column visibility from localStorage:', parsed);
        return parsed;
      } catch (error) {
        console.error('Error parsing saved column visibility:', error);
      }
    }
    console.log('Using default column visibility');
    return {
      item: true,
      objective: true,
      notes: true,
      score: true,
      value: true,
      credit: true
    };
  });

  // Page system state
  const [currentPage, setCurrentPage] = useState(1);
  
  // Page definitions - each page has its own content and row range
  const pageDefinitions = [
    {
      id: 1,
      title: "Scenario Setup",
      description: "Scenario information with main table",
      rowStart: 0,
      rowEnd: -1, // Show all rows on page 1
      showInfoContainers: true,
      showTable: true
    },
    {
      id: 2,
      title: "Scene Size Up",
      description: "Initial scene assessment and primary actions (Rows 1-22)",
      rowStart: 0,
      rowEnd: 21, // Show rows 0-21 (first 22 rows)
      showInfoContainers: false,
      showTable: true
    },
    {
      id: 3,
      title: "Secondary Assessment", 
      description: "Detailed patient assessment (Rows 23-25)",
      rowStart: 22,
      rowEnd: 24, // Show rows 22-24 (3 rows for secondary assessment)
      showInfoContainers: false,
      showTable: true
    }
  ];

  const currentPageDef = pageDefinitions.find(p => p.id === currentPage) || pageDefinitions[0];

  // Load user preferences from Firebase
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (currentUser?.uid) {
        try {
          const userPrefsRef = doc(db, 'userPreferences', currentUser.uid);
          const userPrefsSnap = await getDoc(userPrefsRef);
          
          if (userPrefsSnap.exists()) {
            const preferences = userPrefsSnap.data();
            if (preferences.scenarioEditor?.visibleColumns) {
              console.log('Loaded column visibility from Firebase:', preferences.scenarioEditor.visibleColumns);
              setVisibleColumns(preferences.scenarioEditor.visibleColumns);
              // Also save to localStorage for faster loading
              localStorage.setItem('scenarioEditor-visibleColumns', JSON.stringify(preferences.scenarioEditor.visibleColumns));
            }
          }
        } catch (error) {
          console.error('Error loading user preferences:', error);
        }
      }
    };

    loadUserPreferences();
  }, [currentUser?.uid]);

  // Save preferences to Firebase
  const saveUserPreferences = async (newVisibleColumns) => {
    if (currentUser?.uid) {
      try {
        const userPrefsRef = doc(db, 'userPreferences', currentUser.uid);
        await setDoc(userPrefsRef, {
          scenarioEditor: {
            visibleColumns: newVisibleColumns
          }
        }, { merge: true });
        console.log('Saved column visibility to Firebase:', newVisibleColumns);
      } catch (error) {
        console.error('Error saving user preferences:', error);
      }
    }
  };
  const [showColumnMenu, setShowColumnMenu] = useState(false);

  // Helper function to get the last visible column
  const getLastVisibleColumn = () => {
    const columnOrder = ['item', 'objective', 'notes', 'score', 'value'];
    for (let i = columnOrder.length - 1; i >= 0; i--) {
      if (visibleColumns[columnOrder[i]]) {
        return columnOrder[i];
      }
    }
    return null;
  };

  const isLastVisible = (column) => getLastVisibleColumn() === column;

  // UI state
  const [loading, setLoading] = useState(!!templateId && templateId !== 'new' && templateId !== null);
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [message, setMessage] = useState('');
  
  // Section Total Mode states - Excel-like selection system
  const [sectionTotalMode, setSectionTotalMode] = useState(false);
  const [selectedRowsForTotal, setSelectedRowsForTotal] = useState([]);
  const [pendingSectionStart, setPendingSectionStart] = useState(null);
  const [selectionRange, setSelectionRange] = useState({ start: null, end: null, preview: [] });

  // Load specific template if templateId is provided
  useEffect(() => {
    if (templateId === 'new' || !templateId) {
      // Reset to default template for new scenarios and set creation info
      const newTemplate = { 
        ...defaultTemplate,
        createdBy: currentUser?.email || 'Unknown',
        creationDate: new Date().toLocaleDateString()
      };
      setTemplate(newTemplate);
      setEditingField(null);
      setTempValue('');
      setEditingCell(null);
      setTempCellValue('');
      setMessage('');
      setAutoSaving(false);
      setLoading(false);
      return;
    }
    
    const fetchTemplate = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, 'scenarioTemplates', templateId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setTemplate({ id: templateId, ...docSnap.data() });
        } else {
          alert('Template not found');
          navigate('/scenario-builder');
        }
      } catch (error) {
        console.error('Error loading template:', error);
        alert('Error loading template');
      } finally {
        setLoading(false);
      }
    };
    fetchTemplate();
  }, [templateId, navigate]);

  // Close column menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showColumnMenu && !event.target.closest('.column-menu-container')) {
        setShowColumnMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColumnMenu]);

  // Tailwind-based resize handle logic - updates ratio for responsive classes
  const handleMouseDown = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    
    const startX = e.clientX;
    const startRatio = itemWidthRatio;
    const containerWidth = e.target.closest('.table-container').offsetWidth;
    const contentWidth = containerWidth - 48 - 64; // minus handles and actions
    
    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const deltaRatio = deltaX / contentWidth;
      
      if (type === 'item') {
        // Dragging Item column resize handle
        const newRatio = Math.max(0.1, Math.min(0.9, startRatio + deltaRatio));
        setItemWidthRatio(newRatio);
        localStorage.setItem('scenarioEditor-itemWidthRatio', newRatio.toString());
      } else if (type === 'objective') {
        // Dragging Objective column resize handle
        const newRatio = Math.max(0.1, Math.min(0.9, startRatio - deltaRatio));
        setItemWidthRatio(newRatio);
        localStorage.setItem('scenarioEditor-itemWidthRatio', newRatio.toString());
      }
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Cancel handler to clear state and navigate back
  const handleCancelAndExit = () => {
    // Reset all editing states
    setEditingField(null);
    setTempValue('');
    setEditingCell(null);
    setTempCellValue('');
    setMessage('');
    
    // Navigate back to scenario builder
    navigate('/scenario-builder');
  };

  // Field editing handlers
  const handleEdit = (field, value) => {
    // Prevent editing if another field is already being edited
    if (editingField) {
      return;
    }
    setEditingField(field);
    setTempValue(value);
  };

  const handleConfirm = () => {
    setTemplate(prev => ({ ...prev, [editingField]: tempValue }));
    setEditingField(null);
    setTempValue('');
  };

  const handleCancel = () => {
    setEditingField(null);
    setTempValue('');
  };

  // Cell editing handlers
  const handleCellEdit = (rowIndex, field, value) => {
    // Prevent editing if another cell is already being edited
    if (editingCell) {
      return;
    }
    setEditingCell({ rowIndex, field });
    setTempCellValue(value);
  };

  const handleCellConfirm = () => {
    const newRows = [...template.rows];
    const row = newRows[editingCell.rowIndex];
    
    // Handle stacked objective fields
    if (editingCell.field.startsWith('stacked_')) {
      const parts = editingCell.field.split('_');
      if (parts.length === 3) {
        const fieldType = parts[1]; // 'objective1', 'objective2', 'notes', 'score', 'value'
        const objIndex = parseInt(parts[2]);
        
        if (row.moduleType === 'stacked-row' && row.stackedObjectives && row.stackedObjectives[objIndex]) {
          row.stackedObjectives[objIndex][fieldType] = tempCellValue;
        }
      }
    } else {
      // Handle regular fields
      row[editingCell.field] = tempCellValue;
    }
    
    setTemplate(prev => ({ ...prev, rows: newRows }));
    setEditingCell(null);
    setTempCellValue('');
  };

  const handleCellCancel = () => {
    setEditingCell(null);
    setTempCellValue('');
  };

  // Column toggle handlers
  const toggleColumnVisibility = (column) => {
    setVisibleColumns(prev => {
      const newVisibleColumns = {
        ...prev,
        [column]: !prev[column]
      };
      console.log('Saving column visibility:', newVisibleColumns);
      
      // Save to localStorage immediately for fast response
      localStorage.setItem('scenarioEditor-visibleColumns', JSON.stringify(newVisibleColumns));
      
      // Save to Firebase for cross-device sync
      saveUserPreferences(newVisibleColumns);
      
      return newVisibleColumns;
    });
  };

  // Row manipulation
  const addRow = () => {
    setTemplate(prev => ({
      ...prev,
      rows: [...prev.rows, { item: '', objective: '', notes: '', score: '', value: '', critical: false, credit: false }]
    }));
  };

  const addHeader = () => {
    setTemplate(prev => ({
      ...prev,
      rows: [...prev.rows, { type: 'header', text: 'New Section', item: '', objective: '', notes: '', score: '', value: '', critical: false, credit: false }]
    }));
  };

  const addSubHeader = () => {
    setTemplate(prev => ({
      ...prev,
      rows: [...prev.rows, { type: 'sub-header', text: 'New Sub-Section', item: '', objective: '', notes: '', score: '', value: '', critical: false, credit: false }]
    }));
  };

  const addDescription = () => {
    setTemplate(prev => ({
      ...prev,
      rows: [...prev.rows, { type: 'description', text: 'Add your description here...', item: '', objective: '', notes: '', score: '', value: '', critical: false, credit: false }]
    }));
  };

  const addBlankRow = () => {
    setTemplate(prev => ({
      ...prev,
      rows: [...prev.rows, { type: 'blank-row', item: '', objective: '', notes: '', score: '', value: '', critical: false, credit: false }]
    }));
  };

  const addStackedRow = () => {
    setTemplate(prev => ({
      ...prev,
      rows: [...prev.rows, {
        type: 'row',
        item: '',
        objective: '',
        notes: '',
        score: '',
        value: '',
        critical: false,
        moduleType: 'stacked-row',
        stackedObjectives: [
          { objective1: '', objective2: '', notes: '', score: '', value: '', critical: false, credit: false }
        ]
      }]
    }));
  };

  const handleCustomModuleSelect = (moduleConfig) => {
    // Create a deep copy of the module config to avoid shared references
    const createDeepCopy = (obj) => {
      if (obj === null || typeof obj !== 'object') return obj;
      if (obj instanceof Array) return obj.map(item => createDeepCopy(item));
      return Object.keys(obj).reduce((copy, key) => {
        copy[key] = createDeepCopy(obj[key]);
        return copy;
      }, {});
    };

    const newRowData = createDeepCopy(moduleConfig.defaultData);
    newRowData.moduleType = moduleConfig.id;
    if (newRowData.moduleData) {
      newRowData.moduleData = createDeepCopy(moduleConfig.defaultData.moduleData);
    }

    setTemplate(prev => ({
      ...prev,
      rows: [...prev.rows, newRowData]
    }));
  };

  const removeRow = (index) => {
    if (template.rows.length === 1) return;
    setTemplate(prev => ({
      ...prev,
      rows: prev.rows.filter((_, i) => i !== index)
    }));
  };

  // Section Total Handlers - Excel-like selection system
  const startSectionSelection = (headerIndex, headerRow) => {
    if (headerRow.type !== 'header' && headerRow.type !== 'sub-header') return;
    
    setSectionTotalMode(true);
    setPendingSectionStart({
      headerIndex: headerIndex,
      headerText: headerRow.text || 'Untitled Section'
    });
    setSelectionRange({ start: headerIndex, end: null, preview: [] });
    setSelectedRowsForTotal([]);
    setMessage(`Section started: "${headerRow.text || 'Untitled Section'}". Hover over rows to preview range, click to complete selection.`);
  };

  // Handle mouse hover for range preview
  const handleRowHover = (rowIndex) => {
    if (!pendingSectionStart || selectionRange.start === null) return;
    
    const start = selectionRange.start;
    const end = rowIndex;
    
    // Calculate all rows in the range that should be included
    const preview = [];
    const minIndex = Math.min(start, end);
    const maxIndex = Math.max(start, end);
    
    for (let i = minIndex; i <= maxIndex; i++) {
      const row = template.rows[i];
      if (row && (row.type === 'row' || row.type === 'blank-row' || 
                  (row.moduleType === 'stacked-row') || 
                  (row.type !== 'header' && row.type !== 'sub-header' && row.type !== 'description' && row.type !== 'section-total'))) {
        preview.push(i);
      }
    }
    
    setSelectionRange(prev => ({ ...prev, end: rowIndex, preview }));
  };

  // Handle clicking to complete selection
  const completeRangeSelection = (endIndex) => {
    if (!pendingSectionStart || selectionRange.start === null) return;
    
    setSelectedRowsForTotal(selectionRange.preview);
    setSelectionRange(prev => ({ ...prev, end: endIndex }));
    
    // Automatically create the section total
    if (selectionRange.preview.length > 0) {
      createSectionTotal();
    }
  };

  const createSectionTotal = () => {
    if (!pendingSectionStart || selectedRowsForTotal.length === 0) return;
    
    const lastSelectedIndex = Math.max(...selectedRowsForTotal);
    const newRows = [...template.rows];
    
    // Create the yellow total row
    const totalRow = {
      type: 'section-total',
      headerIndex: pendingSectionStart.headerIndex,
      headerText: pendingSectionStart.headerText,
      includedRows: [...selectedRowsForTotal],
      item: `${pendingSectionStart.headerText} Total`,
      objective: 'Section Total',
      score: '',
      value: '',
      critical: false,
      isAutoCalculated: true
    };
    
    // Insert the total row after the last selected row
    newRows.splice(lastSelectedIndex + 1, 0, totalRow);
    
    setTemplate(prev => ({ ...prev, rows: newRows }));
    
    // Reset section selection state
    setSectionTotalMode(false);
    setPendingSectionStart(null);
    setSelectedRowsForTotal([]);
    setSelectionRange({ start: null, end: null, preview: [] });
    setMessage(`Section total created for "${pendingSectionStart.headerText}" with ${selectedRowsForTotal.length} rows.`);
  };

  const cancelSectionSelection = () => {
    setSectionTotalMode(false);
    setSelectedRowsForTotal([]);
    setPendingSectionStart(null);
    setSelectionRange({ start: null, end: null, preview: [] });
    setMessage('');
  };

  const convertToBlankRow = (index) => {
    setTemplate(prev => ({
      ...prev,
      rows: prev.rows.map((row, i) => 
        i === index 
          ? {
              ...row,
              type: 'blank-row',
              // Keep item, objective, and notes, clear score/value/critical
              score: '',
              value: '',
              critical: false
            }
          : row
      )
    }));
  };

  const convertToRegularRow = (index) => {
    setTemplate(prev => ({
      ...prev,
      rows: prev.rows.map((row, i) => 
        i === index 
          ? {
              ...row,
              type: 'row',
              // Keep all existing content including item, objective, notes
              // Score, value, and critical will retain their existing values or defaults
              score: row.score || '',
              value: row.value || '',
              critical: row.critical || false,
              credit: row.credit || false
            }
          : row
      )
    }));
  };

  const handleRowChange = (index, field, value) => {
    const newRows = [...template.rows];
    newRows[index][field] = value;
    setTemplate(prev => ({ ...prev, rows: newRows }));
  };

  // Helper functions for stacked rows
  const addStackedObjective = (rowIndex) => {
    const newRows = [...template.rows];
    const row = newRows[rowIndex];
    if (row.moduleType === 'stacked-row') {
      if (!row.stackedObjectives) {
        row.stackedObjectives = [];
      }
      row.stackedObjectives.push({
        objective1: '',
        objective2: '',
        notes: '',
        score: '',
        value: '',
        critical: false,
        credit: false
      });
      setTemplate(prev => ({ ...prev, rows: newRows }));
    }
  };

  const removeStackedObjective = (rowIndex, objectiveIndex) => {
    const newRows = [...template.rows];
    const row = newRows[rowIndex];
    if (row.moduleType === 'stacked-row' && row.stackedObjectives) {
      if (row.stackedObjectives.length > 1) {
        row.stackedObjectives.splice(objectiveIndex, 1);
        setTemplate(prev => ({ ...prev, rows: newRows }));
      }
    }
  };

  const handleStackedObjectiveChange = (rowIndex, objectiveIndex, field, value) => {
    const newRows = [...template.rows];
    const row = newRows[rowIndex];
    if (row.moduleType === 'stacked-row' && row.stackedObjectives) {
      row.stackedObjectives[objectiveIndex][field] = value;
      setTemplate(prev => ({ ...prev, rows: newRows }));
    }
  };

  // Duplicate template
  const duplicateTemplate = () => {
    const newName = `${template.name} (Copy)`;
    const duplicatedTemplate = {
      ...template,
      name: newName,
      id: null, // Important: remove ID to make it a new document on save
      createdBy: currentUser?.email || 'Unknown',
      creationDate: new Date().toLocaleDateString(),
      lastModified: serverTimestamp(),
    };
    setTemplate(duplicatedTemplate);
    // Navigate to the 'new' scenario route, but with the state of the duplicated template
    // The user will then save this as a new template.
    navigate('/scenario-builder/new');
    setMessage(`Scenario duplicated. Save to create a new template named "${newName}".`);
  };

  // Save template
  const saveTemplate = async () => {
    if (!template.name.trim()) {
      alert('Please enter a template name');
      return;
    }
    setSaving(true);
    try {
      if (templateId && templateId !== 'new') {
        const docRef = doc(db, 'scenarioTemplates', templateId);
        await setDoc(docRef, template);
        setMessage('Template updated successfully!');
        // Refresh the templates list for updates too
        if (onSave) {
          await onSave();
        }
      } else {
        await addDoc(collection(db, 'scenarioTemplates'), template);
        setMessage('Template saved successfully!');
        
        // Navigate first to ensure proper routing
        navigate('/scenario-builder');
        
        // Then refresh the templates list after a short delay to ensure the parent is rendered
        setTimeout(async () => {
          if (onSave) {
            await onSave();
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error saving template:', error);
      setMessage('Error saving template: ' + error.message);
      alert('Error saving template: ' + error.message);
    }
    setSaving(false);
  };

  // Drag and drop for rows
  const onDragEnd = (result) => {
    try {
      handleElementDragEnd(result, template, setTemplate, startSectionSelection);
    } catch (error) {
      console.error('Drag and drop error:', error);
      // Optionally show user-friendly error message
      setMessage('Error reordering rows. Please try again.');
    }
  };

  // Auto-save effect - saves template changes after 2 seconds of no activity
  // Only auto-saves for existing templates that have been manually saved before
  useEffect(() => {
    if (!templateId || templateId === 'new' || !template.name.trim() || !template.id) return;
    
    const timeoutId = setTimeout(async () => {
      setAutoSaving(true);
      try {
        const docRef = doc(db, 'scenarioTemplates', templateId);
        await setDoc(docRef, template);
        console.log('Auto-saved template changes');
        // Show brief auto-save confirmation
        setTimeout(() => setAutoSaving(false), 1000);
      } catch (error) {
        console.error('Auto-save failed:', error);
        setAutoSaving(false);
      }
    }, 2000); // Auto-save after 2 seconds of inactivity

    return () => clearTimeout(timeoutId);
  }, [template, templateId]);

  // Ensure template has enough rows for page system
  useEffect(() => {
    if (template.rows.length < 25) {
      const newRows = [...template.rows];
      
      // Add Scene Size Up header if it doesn't exist
      if (!newRows.some(row => row.type === 'header' && row.text?.includes('Scene Size Up'))) {
        newRows.push({ type: 'header', text: 'Scene Size Up' });
      }
      
      // Add rows up to 22 for Scene Size Up
      while (newRows.length < 22) {
        newRows.push({ item: '', objective: '', notes: '', score: '', value: '', critical: false, credit: false });
      }
      
      // Add Secondary Assessment header
      if (!newRows.some(row => row.type === 'header' && row.text?.includes('Secondary Assessment'))) {
        newRows.push({ type: 'header', text: 'Secondary Assessment' });
      }
      
      // Add rows up to 25 for Secondary Assessment
      while (newRows.length < 25) {
        newRows.push({ item: '', objective: '', notes: '', score: '', value: '', critical: false, credit: false });
      }
      
      setTemplate(prev => ({ ...prev, rows: newRows }));
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-bg text-text flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <div className="mt-4 text-lg">Loading scenario...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: sidebarStyles }} />
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="bg-slate-200 text-text flex">
          {/* Left Sidebar - positioned below navbar with matching styling */}
          <div 
            className="scenario-sidebar bg-primary/90 backdrop-blur border-r border-primary-light/20 shadow-lg flex flex-col"
            style={{
              transform: `scale(${1/zoomLevel})`,
              transformOrigin: 'top left',
              width: `${16 * zoomLevel}rem`,
              height: `calc(${100 * zoomLevel}vh - ${4 * zoomLevel}rem)`
            }}
          >
          {/* Sidebar Header */}
          <div className="p-4 border-b border-primary-light/20">
            <h2 className="text-lg font-bold text-text">🎭 Scenario Tools</h2>
          </div>
          
          {/* Scrollable Content */}
          <div className="sidebar-content flex-1 overflow-y-auto space-y-6">
            
            {/* Add Elements Section */}
            <div>
              <h3 className="text-sm font-semibold text-text/70 uppercase tracking-wide mb-3">Add Elements</h3>
              <Droppable droppableId="sidebar-elements" isDropDisabled={true}>
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                    <Draggable draggableId="add-row" index={0}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`sidebar-button bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 hover:shadow-lg font-semibold transition-all duration-200 flex items-center gap-3 cursor-grab ${
                            snapshot.isDragging ? 'opacity-50' : ''
                          }`}
                          title="Drag to add Row"
                          onClick={!snapshot.isDragging ? addRow : undefined}
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                          Add Row
                        </div>
                      )}
                    </Draggable>
                    
                    <Draggable draggableId="add-header" index={1}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`sidebar-button bg-emerald-600 text-white rounded-lg shadow-md hover:bg-emerald-700 hover:shadow-lg font-semibold transition-all duration-200 flex items-center gap-3 cursor-grab ${
                            snapshot.isDragging ? 'opacity-50' : ''
                          }`}
                          title="Drag to add Header"
                          onClick={!snapshot.isDragging ? addHeader : undefined}
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                          Add Header
                        </div>
                      )}
                    </Draggable>
                    
                    <Draggable draggableId="add-subheader" index={2}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`sidebar-button bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 hover:shadow-lg font-semibold transition-all duration-200 flex items-center gap-3 cursor-grab ${
                            snapshot.isDragging ? 'opacity-50' : ''
                          }`}
                          title="Drag to add Sub-Header"
                          onClick={!snapshot.isDragging ? addSubHeader : undefined}
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                          Add Sub-Header
                        </div>
                      )}
                    </Draggable>
                    
                    <Draggable draggableId="add-description" index={3}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`sidebar-button bg-amber-600 text-white rounded-lg shadow-md hover:bg-amber-700 hover:shadow-lg font-semibold transition-all duration-200 flex items-center gap-3 cursor-grab ${
                            snapshot.isDragging ? 'opacity-50' : ''
                          }`}
                          title="Drag to add Description"
                          onClick={!snapshot.isDragging ? addDescription : undefined}
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V4H6z" clipRule="evenodd" />
                            <path d="M8 6h4v2H8V6zm0 4h4v2H8v-2z" />
                          </svg>
                          Add Description
                        </div>
                      )}
                    </Draggable>
                    
                    <Draggable draggableId="add-blank-row" index={4}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`sidebar-button bg-slate-600 text-white rounded-lg shadow-md hover:bg-slate-700 hover:shadow-lg font-semibold transition-all duration-200 flex items-center gap-3 cursor-grab ${
                            snapshot.isDragging ? 'opacity-50' : ''
                          }`}
                          title="Drag to add Blank Row"
                          onClick={!snapshot.isDragging ? addBlankRow : undefined}
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                          Add Blank Row
                        </div>
                      )}
                    </Draggable>
                    
                    <Draggable draggableId="add-stacked-row" index={5}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`sidebar-button bg-purple-600 text-white rounded-lg shadow-md hover:bg-purple-700 hover:shadow-lg font-semibold transition-all duration-200 flex items-center gap-3 cursor-grab ${
                            snapshot.isDragging ? 'opacity-50' : ''
                          }`}
                          title="Drag to add Stacked Row"
                          onClick={!snapshot.isDragging ? addStackedRow : undefined}
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                            <path d="M6 6h2v2H6V6zm0 4h2v2H6v-2zm4-4h2v2h-2V6zm0 4h2v2h-2v-2z" />
                          </svg>
                          Add Stacked Row
                        </div>
                      )}
                    </Draggable>
                    
                    <Draggable draggableId="add-section-total" index={6}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`sidebar-button bg-yellow-600 text-white rounded-lg shadow-md hover:bg-yellow-700 hover:shadow-lg font-semibold transition-all duration-200 flex items-center gap-3 cursor-grab ${
                            snapshot.isDragging ? 'opacity-50' : ''
                          }`}
                          title="Drag onto a Header/Sub-Header to start Excel-like range selection for section totals"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 6V4a2 2 0 012-2h4a2 2 0 012 2v2h4a2 2 0 012 2v6a2 2 0 01-2 2H2a2 2 0 01-2-2V8a2 2 0 012-2h4zM6 8H2v6h16V8h-4v1a1 1 0 11-2 0V8H8v1a1 1 0 11-2 0V8zm2-2V4h4v2H8z" clipRule="evenodd" />
                            <path d="M6 12h2v2H6v-2zm6 0h2v2h-2v-2z" />
                          </svg>
                          Section Total
                        </div>
                      )}
                    </Draggable>
                    
                    <button 
                      onClick={() => setShowCustomModuleModal(true)} 
                      className="sidebar-button bg-purple-600 text-white rounded-lg shadow-md hover:bg-purple-700 hover:shadow-lg font-semibold transition-all duration-200 flex items-center gap-3"
                      title="Add Custom Module"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                      Add Module
                    </button>
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>

            {/* Actions Section */}
            <div>
              <h3 className="text-sm font-semibold text-text/70 uppercase tracking-wide mb-3">Actions</h3>
              <div className="space-y-2">
                <button 
                  onClick={saveTemplate} 
                  disabled={saving} 
                  className="sidebar-button bg-green-600 text-white rounded-lg shadow-md hover:bg-green-700 hover:shadow-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z"/>
                      </svg>
                      Save Scenario
                    </>
                  )}
                </button>
                
                {templateId && templateId !== 'new' && (
                  <button
                    onClick={duplicateTemplate}
                    className="sidebar-button bg-blue-500 text-white rounded-lg shadow-md hover:bg-blue-600 hover:shadow-lg font-semibold transition-all duration-200 flex items-center gap-3"
                    title="Duplicate Scenario"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z"></path>
                      <path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2h1V5a2 2 0 012-2h6V3H5z"></path>
                    </svg>
                    Duplicate
                  </button>
                )}

                <button 
                  onClick={handleCancelAndExit} 
                  className="sidebar-button bg-gray-500 text-white rounded-lg shadow-md hover:bg-gray-600 hover:shadow-lg font-semibold transition-all duration-200 flex items-center gap-3"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Cancel
                </button>
                
                {templateId && templateId !== 'new' && (
                  <button 
                    onClick={() => generatePDF(template, 'portrait')} 
                    className="sidebar-button bg-emerald-600 text-white rounded-lg shadow-md hover:bg-emerald-700 hover:shadow-lg font-semibold transition-all duration-200 flex items-center gap-3"
                    title="Download PDF"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 011.414 0l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Download PDF
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

          {/* Main Content */}
          <div 
            className="scenario-main-content flex flex-col items-center pt-20 pb-8 px-4"
            style={{
              marginLeft: `${16}rem`
            }}
          >
          <div className="flex items-center gap-4 mb-4">
          <h1 className="text-2xl font-bold text-primary">🎭 Scenario Editor</h1>
          {autoSaving && (
            <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-1 rounded-full border border-green-200">
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Auto-saving...
            </div>
          )}
        </div>

        {/* Section Total Mode Banner */}
        {(sectionTotalMode || pendingSectionStart) && (
          <div className="w-full max-w-6xl mb-4 bg-yellow-100 border-2 border-yellow-400 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="text-yellow-600">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 6V4a2 2 0 012-2h4a2 2 0 012 2v2h4a2 2 0 012 2v6a2 2 0 01-2 2H2a2 2 0 01-2-2V8a2 2 0 012-2h4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-yellow-800">Excel-Style Section Total Selection</h3>
                  <p className="text-sm text-yellow-700">
                    {!pendingSectionStart 
                      ? "Drag the 'Section Total' element from the sidebar onto any Header or Sub-Header to start range selection"
                      : `Section: "${pendingSectionStart.headerText}" | Hover over rows to preview range, click to complete selection | Preview: ${selectionRange.preview.length} rows`
                    }
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={cancelSectionSelection}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Page Navigation */}
        <div className="w-full max-w-6xl mb-4">
          <div className="flex justify-center gap-2">
            {pageDefinitions.map((page) => (
              <button
                key={page.id}
                onClick={() => setCurrentPage(page.id)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  currentPage === page.id
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                }`}
              >
                <div className="text-center">
                  <div className="font-bold">{page.title}</div>
                  <div className="text-xs opacity-80">{page.description}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Current Page Header */}
        <div className="w-full max-w-6xl mb-4 text-center">
          <h2 className="text-xl font-bold text-gray-800">{currentPageDef.title}</h2>
          <p className="text-sm text-gray-600">{currentPageDef.description}</p>
        </div>
        
        <div className="w-full max-w-6xl bg-table p-6 rounded-xl border border-primary mb-6 shadow-lg">
          {/* Conditionally render info containers only on page 1 */}
          {currentPageDef.showInfoContainers && (
            <div className="space-y-3 mb-4">
              {/* Scenario Info Container - Full Width */}
              <div className="w-full">
                {/* Scenario Info Container */}
                {/* STYLING: Scenario details form - now full width */}
                {/* CUSTOMIZE: Border (border-gray-200), background, spacing */}
                <div className="flex flex-col px-4 py-3 border border-gray-200 rounded-lg">
                  {/* STYLING: Section title */}
                  <h2 className="text-lg font-bold text-primary border-b py-2 px-1 mb-3 text-center">Scenario Information</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                    {['name', 'scenarioCategory', 'description'].map(field => {
                      const labels = { name: 'Scenario Name', scenarioCategory: 'Scenario Category', description: 'Description' };
                      return (
                        <div key={field} className={`flex items-center ${field === 'description' ? 'md:col-span-2' : ''}`}>
                          {/* STYLING: Field label - fixed width for alignment */}
                          <label className="font-semibold text-gray-700 w-[130px] text-sm leading-tight flex-shrink-0 text-left">{labels[field]}:</label>
                          <div className="flex-1 relative">
                            {editingField === field ? (
                              <HeaderEditor
                                value={tempValue}
                                onChange={setTempValue}
                                onConfirm={handleConfirm}
                                onCancel={handleCancel}
                                isTextarea={field === 'description'}
                                placeholder={`Enter ${labels[field].toLowerCase()}...`}
                              />
                            ) : (
                              <div 
                                className={`px-3 py-2 min-h-[32px] text-sm leading-tight border border-gray-200 hover:border-blue-400 cursor-pointer rounded bg-gray-50 hover:bg-gray-100 transition-all duration-200 break-words whitespace-pre-wrap text-left ${editingField ? 'pointer-events-none opacity-50' : ''}`}
                                onClick={() => handleEdit(field, template[field])}
                              >
                                {template[field] || <span className="text-gray-400 italic">Click to add {labels[field].toLowerCase()}</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Read-only fields for Created By and Creation Date */}
                    {/* STYLING: Read-only form fields with gray background */}
                    <div className="flex items-center">
                      <label className="font-semibold text-gray-700 w-[130px] text-sm leading-tight flex-shrink-0 text-left">Created by:</label>
                      <div className="flex-1 px-3 py-2 text-sm leading-tight border border-gray-200 rounded bg-gray-100 text-gray-700 min-h-[32px]">
                        {template.createdBy || 'Unknown'}
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <label className="font-semibold text-gray-700 w-[130px] text-sm leading-tight flex-shrink-0 text-left">Creation Date:</label>
                      <div className="flex-1 px-3 py-2 text-sm leading-tight border border-gray-200 rounded bg-gray-100 text-gray-700 min-h-[32px]">
                        {template.creationDate || 'Unknown'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Scenario Table */}
          {/* STYLING: Main table container with controls and data grid */}
          <div className="w-full relative">
            
            {/* STYLING: Main data table with Tailwind responsive grid */}
            {/* CUSTOMIZE: Border (border-gray-300), table layout, scroll behavior */}
            <div className="overflow-x-auto border border-gray-300 border-b-1 rounded-2xl table-container">
              <table className="w-full border-collapse rounded-xl shadow-sm text-sm leading-snug overflow-hidden">
                <thead>
                  {/* STYLING: Table header row */}
                  {/* CUSTOMIZE: Background (bg-gray-200), text colors, border */}
                  <tr className="bg-gray-200 text-gray-800 border-b-2 border-gray-300">
                    {/* STYLING: Drag handle column - fixed narrow width */}
                    <th className="text-center font-semibold w-6 min-w-6 max-w-6"></th>
                    {/* STYLING: Row number column - fixed narrow width */}
                    <th className="text-center font-semibold w-6 min-w-6 max-w-6">#</th>
                    <th className={`relative text-center text-sm font-bold text-gray-700 ${getItemWidthClass()}`}>
                      Item
                      {/* STYLING: Column resize handle */}
                      {/* CUSTOMIZE: Handle color (bg-gray-300), hover effect */}
                      <div 
                        className="absolute right-0 top-0 bottom-0 w-4 flex items-center justify-center bg-gray-300 hover:bg-gray-400 cursor-col-resize opacity-70 hover:opacity-100 transition-all duration-200 border-l border-gray-400"
                        onMouseDown={(e) => handleMouseDown(e, 'item')}
                        title="Drag to resize column"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5h2v14H8V5zm6 0h2v14h-2V5z"/>
                        </svg>
                      </div>
                    </th>
                    <th className={`relative text-center text-sm font-bold text-gray-700 ${getObjectiveWidthClass()}`} colSpan={2}>
                      Objective
                      <div 
                        className="absolute right-0 top-0 bottom-0 w-4 flex items-center justify-center bg-gray-300 hover:bg-gray-400 cursor-col-resize opacity-70 hover:opacity-100 transition-all duration-200 border-l border-gray-400"
                        onMouseDown={(e) => handleMouseDown(e, 'objective')}
                        title="Drag to resize column"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5h2v14H8V5zm6 0h2v14h-2V5z"/>
                        </svg>
                      </div>
                    </th>

                    {/* Actions Column */}
                    <th className="text-center align-middle text-xs font-bold text-gray-700 sticky right-0 bg-gray-200 border-l-2 border-gray-400 shadow-lg z-10 w-16 min-w-16 max-w-16">
                      ⚙️
                    </th>
                  </tr>
                </thead>
                <Droppable droppableId="scenario-rows">
                  {(provided, snapshot) => (
                    <tbody 
                      {...provided.droppableProps} 
                      ref={provided.innerRef}
                      className={snapshot.isDraggingOver ? 'bg-blue-50 transition-colors' : ''}
                    >
                      {template.rows
                        .filter((row, idx) => {
                          // For page 1, show all rows
                          if (currentPageDef.id === 1) return true;
                          
                          // For other pages, filter by row range
                          if (currentPageDef.rowEnd === -1) return true; // Show all if no end specified
                          return idx >= currentPageDef.rowStart && idx <= currentPageDef.rowEnd;
                        })
                        .map((row, filteredIdx) => {
                          // Calculate original index for drag and drop
                          const originalIdx = template.rows.indexOf(row);
                          return (
                        <Draggable key={originalIdx} draggableId={`row-${originalIdx}`} index={originalIdx}>
                          {(provided, snapshot) => {
                            // ==========================================
                            // SECTION HEADER ROW (type: 'header')
                            // STYLING: Main section dividers with indigo theme
                            // CUSTOMIZE: Background (bg-indigo-50), border colors, text styles
                            // ==========================================
                            if (row.type === 'header') {
                              const sectionTotal = row.hasSectionTotal ? calculateSectionTotal(template.rows, originalIdx) : null;
                              
                              return (
                                <tr 
                                  {...provided.draggableProps} 
                                  ref={provided.innerRef} 
                                  className="group relative bg-indigo-50 border-b-2 border-indigo-200 hover:bg-indigo-100 transition-colors duration-200"
                                  data-row-index={originalIdx}
                                >
                                  {/* STYLING: Drag handle with indigo theme */}
                                  <td {...provided.dragHandleProps} className="cursor-grab text-center align-middle text-indigo-600 font-bold text-lg py-0 relative w-6 min-w-6 max-w-6">
                                    ⠿
                                  </td>
                                  {/* STYLING: Header indicator badge */}
                                  <td className="text-center align-middle font-bold text-indigo-800 text-sm bg-indigo-100 py-0 px-1 w-6 min-w-6 max-w-6">
                                    {row.hasSectionTotal ? '∑' : 'H'}
                                  </td>
                                  {/* STYLING: Header text cell - spans from Item to Objective columns */}
                                  <td colSpan={3} className="bg-indigo-50 relative py-0 px-1 uppercase">
                                    <div className="flex items-center justify-between">
                                      <div className="flex-1">
                                        <EditableCell
                                          value={row.text}
                                          onChange={(value) => handleRowChange(originalIdx, 'text', value)}
                                          onConfirm={handleCellConfirm}
                                          onCancel={handleCellCancel}
                                          isHeader={true}
                                          rowIndex={originalIdx}
                                          field="text"
                                          editingCell={editingCell}
                                          setEditingCell={setEditingCell}
                                          tempCellValue={tempCellValue}
                                          setTempCellValue={setTempCellValue}
                                        />
                                      </div>
                                      {sectionTotal && (
                                        <div className="ml-4 text-xs font-bold text-indigo-700 bg-indigo-100 px-2 py-1 rounded">
                                          {sectionTotal.config.scoreTypes.includes('score') && (
                                            <span className="mr-2">Score: {sectionTotal.totals.score.toFixed(1)}</span>
                                          )}
                                          {sectionTotal.config.scoreTypes.includes('value') && (
                                            <span>Value: {sectionTotal.totals.value.toFixed(1)}</span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  
                                  {/* STYLING: Delete button for headers */}
                                  {/* CUSTOMIZE: Button color (text-red-500), hover effects */}
                                  <td className="text-center align-middle bg-indigo-50 py-0 px-1 sticky right-0 border-l-2 border-gray-400 shadow-lg z-10 w-16 min-w-16 max-w-16">
                                    <div className="flex items-center justify-center gap-0 flex-col">
                                      {row.hasSectionTotal && (
                                        <button 
                                          type="button" 
                                          className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 p-0.5 rounded-full transition-all duration-200"
                                          onClick={() => {
                                            // TODO: Open section total configuration modal
                                            console.log('Configure section total for:', row.text);
                                          }}
                                          title="Configure Section Total"
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                                          </svg>
                                        </button>
                                      )}
                                      <button 
                                        type="button" 
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-0.5 rounded-full transition-all duration-200 group-hover:opacity-100 opacity-70"
                                        onClick={() => removeRow(originalIdx)} 
                                        title="Delete Header"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                                        </svg>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            }

                            // ==========================================
                            // SUB-HEADER ROW (type: 'sub-header')
                            // STYLING: Sub-section dividers with purple theme
                            // CUSTOMIZE: Background (bg-purple-50), border colors
                            // ==========================================
                            if (row.type === 'sub-header') {
                              // Check if this is a custom module sub-header (but not stacked-row which is handled as regular row)
                              if (isCustomModule(row) && row.moduleType !== 'stacked-row') {
                                return (
                                  <Fragment key={`sub-header-module-${originalIdx}`}>
                                    {/* Sub-header row */}
                                    <tr 
                                      {...provided.draggableProps} 
                                      ref={provided.innerRef} 
                                      className="group relative hover:bg-purple-25 transition-colors duration-200"
                                      data-row-index={originalIdx}
                                    >
                                      <td {...provided.dragHandleProps} className="cursor-grab text-center text-purple-600 bg-purple-50 py-0 px-1 font-bold relative w-6 min-w-6 max-w-6">
                                        ⠿
                                      </td>
                                      <td className="text-center font-bold text-purple-700 text-xs bg-purple-100 py-0 px-1 w-6 min-w-6 max-w-6">MOD</td>
                                      <td colSpan={3} className="bg-purple-50 relative py-0 px-1 text-center uppercase shadow-inner border-t border-l border-purple-300/60 bg-gradient-to-br from-purple-100 to-purple-50">
                                        <EditableCell
                                          value={row.text}
                                          onChange={(value) => handleRowChange(originalIdx, 'text', value)}
                                          onConfirm={handleCellConfirm}
                                          onCancel={handleCellCancel}
                                          isHeader={true}
                                          isSubHeader={true}
                                          rowIndex={originalIdx}
                                          field="text"
                                          editingCell={editingCell}
                                          setEditingCell={setEditingCell}
                                          tempCellValue={tempCellValue}
                                          setTempCellValue={setTempCellValue}
                                        />
                                      </td>
                                      <td className="text-center align-middle bg-purple-50 py-0 px-1 sticky right-0 border-l-2 border-gray-400 shadow-lg z-10 w-16 min-w-16 max-w-16">
                                        <button 
                                          type="button" 
                                          className="text-red-500 hover:text-red-700 hover:bg-red-50 p-0.5 rounded-full transition-all duration-200 group-hover:opacity-100 opacity-70"
                                          onClick={() => removeRow(originalIdx)} 
                                          title="Delete Module"
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                                          </svg>
                                        </button>
                                      </td>
                                    </tr>
                                    {/* Module content row */}
                                    <tr className="group relative bg-gray-50">
                                      <td className="text-center"></td>
                                      <td className="text-center"></td>
                                      {/* Custom module content spanning item and objective columns */}
                                      <td colSpan={3} className="border-r border-gray-300 text-sm py-0 px-1">
                                        {renderCustomModule(row, (field, value) => handleRowChange(originalIdx, field, value), false, {
                                          editingCell,
                                          setEditingCell,
                                          tempCellValue,
                                          setTempCellValue,
                                          onConfirm: handleCellConfirm,
                                          onCancel: handleCellCancel,
                                          rowIndex: originalIdx
                                        })}
                                      </td>
                                      
                                      {/* Actions Cell */}
                                      <td className="text-center py-0 sticky right-0 bg-gray-50 border-l-2 border-gray-400 shadow-lg z-10 w-16 min-w-16 max-w-16">
                                        <div className="flex items-center justify-center">
                                          <button
                                            onClick={() => removeRow(originalIdx)}
                                            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-0.5 rounded transition-all duration-200"
                                            title="Delete Module"
                                          >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                                            </svg>
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  </Fragment>
                                );
                              } else {
                                // Regular sub-header
                                const sectionTotal = row.hasSectionTotal ? calculateSectionTotal(template.rows, originalIdx) : null;
                                
                                return (
                                  <tr 
                                    {...provided.draggableProps} 
                                    ref={provided.innerRef} 
                                    className="group relative hover:bg-purple-25 transition-colors duration-200"
                                    data-row-index={originalIdx}
                                  >
                                    <td {...provided.dragHandleProps} className="cursor-grab text-center text-purple-600 bg-purple-50 py-0 px-1 font-bold relative w-6 min-w-6 max-w-6">
                                      ⠿
                                    </td>
                                    <td className="text-center font-bold text-purple-700 text-xs bg-purple-100 py-0 px-1 w-6 min-w-6 max-w-6">
                                      {row.hasSectionTotal ? '∑' : 'SH'}
                                    </td>
                                    <td colSpan={3} className="bg-purple-50 relative py-0 px-1 text-center uppercase shadow-inner border-t border-l border-purple-300/60 bg-gradient-to-br from-purple-100 to-purple-50">
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                          <EditableCell
                                            value={row.text}
                                            onChange={(value) => handleRowChange(originalIdx, 'text', value)}
                                            onConfirm={handleCellConfirm}
                                            onCancel={handleCellCancel}
                                            isHeader={true}
                                            isSubHeader={true}
                                            rowIndex={originalIdx}
                                            field="text"
                                            editingCell={editingCell}
                                            setEditingCell={setEditingCell}
                                            tempCellValue={tempCellValue}
                                            setTempCellValue={setTempCellValue}
                                          />
                                        </div>
                                        {sectionTotal && (
                                          <div className="ml-4 text-xs font-bold text-purple-700 bg-purple-100 px-2 py-1 rounded">
                                            {sectionTotal.config.scoreTypes.includes('score') && (
                                              <span className="mr-2">Score: {sectionTotal.totals.score.toFixed(1)}</span>
                                            )}
                                            {sectionTotal.config.scoreTypes.includes('value') && (
                                              <span>Value: {sectionTotal.totals.value.toFixed(1)}</span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                    <td className="text-center align-middle bg-purple-50 py-0 px-1 sticky right-0 border-l-2 border-gray-400 shadow-lg z-10 w-16 min-w-16 max-w-16">
                                      <div className="flex items-center justify-center gap-0 flex-col">
                                        {row.hasSectionTotal && (
                                          <button 
                                            type="button" 
                                            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 p-0.5 rounded-full transition-all duration-200"
                                            onClick={() => {
                                              // TODO: Open section total configuration modal
                                              console.log('Configure section total for:', row.text);
                                            }}
                                            title="Configure Section Total"
                                          >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                              <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                                            </svg>
                                          </button>
                                        )}
                                        <button 
                                          type="button" 
                                          className="text-red-500 hover:text-red-700 hover:bg-red-50 p-0.5 rounded-full transition-all duration-200 group-hover:opacity-100 opacity-70"
                                          onClick={() => removeRow(originalIdx)} 
                                          title="Delete Sub-Header"
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                                          </svg>
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              }
                            }
                            
                            // ==========================================
                            // DESCRIPTION ROW (type: 'description')
                            // STYLING: Descriptive text rows with amber theme and centered italic text
                            // CUSTOMIZE: Background (bg-amber-50), text styles (italic, centered)
                            // ==========================================
                            if (row.type === 'description') {
                              return (
                                <tr 
                                  {...provided.draggableProps} 
                                  ref={provided.innerRef} 
                                  className="group relative bg-amber-50 border-b-2 border-amber-200 hover:bg-amber-100 transition-colors duration-200"
                                  data-row-index={originalIdx}
                                >
                                  {/* STYLING: Drag handle with amber theme */}
                                  <td {...provided.dragHandleProps} className="cursor-grab text-center align-middle text-amber-600 font-bold text-lg py-1 w-6 min-w-6 max-w-6">⠿</td>
                                  {/* STYLING: Description indicator badge */}
                                  <td className="text-center align-middle font-bold text-amber-800 text-sm bg-amber-100 py-1 px-1 w-6 min-w-6 max-w-6">DESC</td>
                                  {/* STYLING: Description text cell - spans from Item to Critical columns */}
                                  <td colSpan={3} className="bg-amber-50 relative py-1 px-4 text-center italic">
                                    <EditableCell
                                      value={row.text}
                                      onChange={(value) => handleRowChange(originalIdx, 'text', value)}
                                      onConfirm={handleCellConfirm}
                                      onCancel={handleCellCancel}
                                      isHeader={false}
                                      isDescription={true}
                                      rowIndex={originalIdx}
                                      field="text"
                                      editingCell={editingCell}
                                      setEditingCell={setEditingCell}
                                      tempCellValue={tempCellValue}
                                      setTempCellValue={setTempCellValue}
                                    />
                                  </td>
                                  
                                  {/* STYLING: Delete button for descriptions */}
                                  <td className="text-center align-middle bg-amber-50 py-1 px-1 sticky right-0 border-l-2 border-gray-400 shadow-lg z-10 w-16 min-w-16 max-w-16">
                                    <button 
                                      type="button" 
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50 p-0.5 rounded-full transition-all duration-200 group-hover:opacity-100 opacity-70"
                                      onClick={() => removeRow(originalIdx)} 
                                      title="Delete Description"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                                      </svg>
                                    </button>
                                  </td>
                                </tr>
                              );
                            }
                            
                            // Skip custom modules that are sub-headers - they're already handled above (except stacked-row)
                            if (isCustomModule(row) && row.type === 'sub-header' && row.moduleType !== 'stacked-row') {
                              return null;
                            }
                            
                            // ==========================================
                            // BLANK ROW (type: 'blank-row')
                            // STYLING: Simple row with only Item and Objective columns
                            // CUSTOMIZE: Background, no score/value/critical columns
                            // ==========================================
                            if (row.type === 'blank-row') {
                              // Calculate row number accounting for stacked rows  
                              let rowNumber = 0;
                              for (let i = 0; i <= originalIdx; i++) {
                                const currentRow = template.rows[i];
                                if (currentRow.type !== 'header' && currentRow.type !== 'sub-header' && currentRow.type !== 'description' && currentRow.type !== 'blank-row') {
                                  if (currentRow.moduleType === 'stacked-row' && currentRow.stackedObjectives) {
                                    rowNumber += currentRow.stackedObjectives.length;
                                  } else {
                                    rowNumber += 1;
                                  }
                                }
                              }
                              
                              return (
                                <tr 
                                  {...provided.draggableProps} 
                                  ref={provided.innerRef} 
                                  className={`group relative border-b border-gray-300 hover:shadow-md transition-all duration-200 bg-slate-50 hover:bg-slate-100 ${snapshot.isDragging ? 'shadow-lg bg-white border-blue-400' : ''}`}
                                  data-row-index={originalIdx}
                                >
                                  <td {...provided.dragHandleProps} className={`cursor-grab text-center border-r border-gray-300 text-slate-500 hover:text-slate-700 py-1 w-6 min-w-6 max-w-6`}>⠿</td>
                                  <td className={`text-center border-r border-gray-300 font-semibold text-slate-700 py-1 w-6 min-w-6 max-w-6`}>-</td>
                                  
                                  {/* Item Cell */}
                                  <td className={`font-semibold text-sm cursor-pointer hover:bg-slate-50 transition-all duration-150 border-b border-gray-200 py-1 px-1 border-r border-gray-300 text-blue-800 ${getItemWidthClass()}`}>
                                    <EditableCell
                                      value={row.item}
                                      onChange={(value) => handleRowChange(originalIdx, 'item', value)}
                                      onConfirm={handleCellConfirm}
                                      onCancel={handleCellCancel}
                                      isHeader={false}
                                      rowIndex={originalIdx}
                                      field="item"
                                      editingCell={editingCell}
                                      setEditingCell={setEditingCell}
                                      tempCellValue={tempCellValue}
                                      setTempCellValue={setTempCellValue}
                                    />
                                  </td>
                                  
                                  {/* Objective Cell - spans across both objective columns */}
                                  <td 
                                    className={`text-sm cursor-pointer hover:bg-slate-50 transition-all duration-150 py-0 px-1 border-r border-gray-300 ${getObjectiveWidthClass()}`}
                                    colSpan={2}
                                  >
                                    <EditableCell
                                      value={row.objective}
                                      onChange={(value) => handleRowChange(originalIdx, 'objective', value)}
                                      onConfirm={handleCellConfirm}
                                      onCancel={handleCellCancel}
                                      isHeader={false}
                                      rowIndex={originalIdx}
                                      field="objective"
                                      editingCell={editingCell}
                                      setEditingCell={setEditingCell}
                                      tempCellValue={tempCellValue}
                                      setTempCellValue={setTempCellValue}
                                    />
                                  </td>
                                  
                                  {/* Actions Cell */}
                                  <td className="text-center py-0 sticky right-0 bg-slate-50 border-l-2 border-gray-400 shadow-lg z-10 w-16 min-w-16 max-w-16">
                                    <div className="flex items-center justify-center gap-0 flex-col">
                                      <button
                                        onClick={() => convertToRegularRow(originalIdx)}
                                        className="text-green-500 hover:text-green-700 hover:bg-green-50 p-0.5 rounded transition-all duration-200"
                                        title="Convert to Regular Row"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                        </svg>
                                      </button>
                                      <button
                                        onClick={() => removeRow(originalIdx)}
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-0.5 rounded transition-all duration-200"
                                        title="Delete Blank Row"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                                        </svg>
                                      </button>
                                    </div>
                                  </td>

                                </tr>
                              );
                            }
                            
                            // ==========================================
                            // SECTION TOTAL ROW (type: 'section-total')
                            // STYLING: Yellow background with auto-calculated totals
                            // CUSTOMIZE: Shows section totals in yellow rows
                            // ==========================================
                            if (row.type === 'section-total') {
                              // Calculate the actual total from the included rows
                              const sectionTotal = calculateIncludedRowsTotals(template.rows, row.includedRows);
                              
                              return (
                                <tr 
                                  {...provided.draggableProps} 
                                  ref={provided.innerRef} 
                                  className={`group relative border-b border-gray-300 hover:shadow-md transition-all duration-200 bg-yellow-100 hover:bg-yellow-200 ${snapshot.isDragging ? 'shadow-lg bg-yellow-100 border-yellow-400' : ''}`}
                                  data-row-index={originalIdx}
                                >
                                  <td {...provided.dragHandleProps} className={`cursor-grab text-center border-r border-gray-300 text-yellow-700 hover:text-yellow-800 py-1 w-6 min-w-6 max-w-6`}>⠿</td>
                                  <td className={`text-center border-r border-gray-300 font-bold text-yellow-800 py-1 w-6 min-w-6 max-w-6`}>Σ</td>
                                  
                                  {/* Item Cell - Shows section name */}
                                  <td className={`font-bold text-sm py-1 px-1 border-r border-gray-300 text-yellow-800 ${getItemWidthClass()}`}>
                                    {row.item}
                                  </td>
                                  
                                  {/* Objective Cell */}
                                  <td className={`text-sm py-1 px-1 border-r border-gray-300 text-yellow-800 font-semibold ${getObjectiveWidthClass()}`} colSpan={2}>
                                    {row.objective}
                                  </td>
                                  
                                  {/* Actions Cell */}
                                  <td className="text-center py-1 sticky right-0 bg-yellow-100 border-l-2 border-gray-400 shadow-lg z-10 w-16 min-w-16 max-w-16">
                                    <div className="flex items-center justify-center">
                                      <button
                                        onClick={() => removeRow(originalIdx)}
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-0.5 rounded transition-all duration-200"
                                        title="Delete Section Total"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                                        </svg>
                                      </button>
                                    </div>
                                  </td>

                                </tr>
                              );
                            }
                            
                            // Regular row
                            // Calculate row number accounting for stacked rows
                            let rowNumber = 0;
                            for (let i = 0; i <= originalIdx; i++) {
                              const currentRow = template.rows[i];
                              if (currentRow.type !== 'header' && currentRow.type !== 'sub-header' && currentRow.type !== 'description' && currentRow.type !== 'blank-row' && currentRow.type !== 'section-total') {
                                if (currentRow.moduleType === 'stacked-row' && currentRow.stackedObjectives) {
                                  if (i === originalIdx) {
                                    rowNumber += 1; // Only count the first row of the current stacked row
                                  } else {
                                    rowNumber += currentRow.stackedObjectives.length; // Count all objectives of previous stacked rows
                                  }
                                } else {
                                  rowNumber += 1;
                                }
                              }
                            }
                            
                            // Handle stacked rows
                            if (row.moduleType === 'stacked-row') {
                              const stackedObjectives = row.stackedObjectives || [{ objective1: '', objective2: '', notes: '', score: '', value: '', critical: false, credit: false }];
                              
                              return (
                                <Fragment key={`stacked-row-${originalIdx}`}>
                                  {stackedObjectives.map((objective, objIndex) => {
                                    // Calculate row number for each objective row
                                    // Count all previous rows that contribute to row numbers
                                    let baseRowNumber = 0;
                                    for (let i = 0; i < originalIdx; i++) {
                                      const prevRow = template.rows[i];
                                      if (prevRow.type !== 'header' && prevRow.type !== 'sub-header' && prevRow.type !== 'description' && prevRow.type !== 'blank-row') {
                                        if (prevRow.moduleType === 'stacked-row' && prevRow.stackedObjectives) {
                                          baseRowNumber += prevRow.stackedObjectives.length;
                                        } else {
                                          baseRowNumber += 1;
                                        }
                                      }
                                    }
                                    const currentRowNumber = baseRowNumber + objIndex + 1;
                                    
                                    return (
                                      <tr 
                                        key={`${originalIdx}-${objIndex}`}
                                        {...(objIndex === 0 ? provided.draggableProps : {})}
                                        ref={objIndex === 0 ? provided.innerRef : null}
                                        className={`group relative border-b border-gray-300 hover:shadow-md transition-all duration-200 bg-white hover:bg-gray-50 ${snapshot.isDragging && objIndex === 0 ? 'shadow-lg bg-white border-blue-400' : ''} ${
                                          pendingSectionStart && selectionRange.preview.includes(originalIdx) ? 'bg-blue-100 border-blue-400' : ''
                                        } ${
                                          selectedRowsForTotal.includes(originalIdx) ? 'bg-green-100 border-green-400' : ''
                                        }`}
                                        data-row-index={originalIdx}
                                        onMouseEnter={() => handleRowHover(originalIdx)}
                                        onClick={() => completeRangeSelection(originalIdx)}
                                      >
                                        {/* Drag handle - only on first row */}
                                        <td {...(objIndex === 0 ? provided.dragHandleProps : {})} className="cursor-grab text-center border-r border-gray-300 text-gray-500 hover:text-gray-700 py-1" style={{ width: '24px', minWidth: '24px', maxWidth: '24px' }}>
                                          {objIndex === 0 ? (
                                            pendingSectionStart ? (
                                              <div className={`flex items-center justify-center rounded-sm transition-colors ${
                                                selectionRange.preview.includes(originalIdx) ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'
                                              }`}>
                                                {selectedRowsForTotal.includes(originalIdx) ? '✓' : (selectionRange.preview.includes(originalIdx) ? '◯' : '⠿')}
                                              </div>
                                            ) : '⠿'
                                          ) : ''}
                                        </td>
                                        
                                        {/* Row number - each objective gets its own number */}
                                        <td className="text-center border-r border-gray-300 font-semibold text-gray-700 py-1" style={{ width: '24px', minWidth: '24px', maxWidth: '24px' }}>
                                          {currentRowNumber}
                                        </td>
                                        
                                        {/* Item Cell - only on first row, spans vertically */}
                                        {visibleColumns.item && objIndex === 0 && (
                                          <td 
                                            rowSpan={stackedObjectives.length}
                                            className={`border-r border-gray-300 font-semibold text-sm cursor-pointer hover:bg-blue-50 transition-all duration-150 border-b border-gray-200 py-2 px-2 relative bg-slate-50 text-blue-800 ${getItemWidthClass()}`}
                                            style={{ 
                                              verticalAlign: 'middle',
                                              minHeight: '40px'
                                            }}
                                          >
                                            <div className="w-full h-full min-h-[32px] flex items-center justify-center">
                                              <EditableCell
                                                value={row.item}
                                                onChange={(value) => handleRowChange(originalIdx, 'item', value)}
                                                onConfirm={handleCellConfirm}
                                                onCancel={handleCellCancel}
                                                isHeader={false}
                                                rowIndex={originalIdx}
                                                field="item"
                                                editingCell={editingCell}
                                                setEditingCell={setEditingCell}
                                                tempCellValue={tempCellValue}
                                                setTempCellValue={setTempCellValue}
                                              />
                                            </div>
                                          </td>
                                        )}
                                        
                                        {/* Objective Cells - Split into two */}
                                        {visibleColumns.objective && (
                                          <>
                                            <td className="border-r border-gray-300 text-sm cursor-pointer hover:bg-gray-50 transition-all duration-150 py-0 px-1 w-1/2 font-semibold text-blue-800">
                                              <EditableCell
                                                value={objective.objective1 || ''}
                                                onConfirm={() => {
                                                  handleStackedObjectiveChange(originalIdx, objIndex, 'objective1', tempCellValue);
                                                  setEditingCell(null);
                                                  setTempCellValue('');
                                                }}
                                                onCancel={handleCellCancel}
                                                isHeader={false}
                                                rowIndex={originalIdx}
                                                field={`stacked_objective1_${objIndex}`}
                                                editingCell={editingCell}
                                                setEditingCell={setEditingCell}
                                                tempCellValue={tempCellValue}
                                                setTempCellValue={setTempCellValue}
                                              />
                                            </td>
                                            <td className="border-r border-gray-300 text-sm cursor-pointer hover:bg-gray-50 transition-all duration-150 py-0 px-1 w-1/2">
                                              <EditableCell
                                                value={objective.objective2 || ''}
                                                onConfirm={() => {
                                                  handleStackedObjectiveChange(originalIdx, objIndex, 'objective2', tempCellValue);
                                                  setEditingCell(null);
                                                  setTempCellValue('');
                                                }}
                                                onCancel={handleCellCancel}
                                                isHeader={false}
                                                rowIndex={originalIdx}
                                                field={`stacked_objective2_${objIndex}`}
                                                editingCell={editingCell}
                                                setEditingCell={setEditingCell}
                                                tempCellValue={tempCellValue}
                                                setTempCellValue={setTempCellValue}
                                              />
                                            </td>
                                          </>
                                        )}
                                        
                                        {/* Actions Cell */}
                                        <td className="text-center py-0 sticky right-0 bg-white border-l-2 border-gray-400 shadow-lg z-10 w-16 min-w-16 max-w-16">
                                          <div className="flex items-center justify-center gap-0 flex-col">
                                            {objIndex === 0 && (
                                              <button
                                                onClick={() => addStackedObjective(originalIdx)}
                                                className="text-green-500 hover:text-green-700 hover:bg-green-50 p-0.5 rounded transition-all duration-200"
                                                title="Add Objective"
                                              >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                                </svg>
                                              </button>
                                            )}
                                            
                                            {stackedObjectives.length > 1 && (
                                              <button
                                                onClick={() => removeStackedObjective(originalIdx, objIndex)}
                                                className="text-orange-500 hover:text-orange-700 hover:bg-orange-50 p-0.5 rounded transition-all duration-200"
                                                title="Remove Objective"
                                              >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                              </button>
                                            )}
                                            
                                            {objIndex === 0 && (
                                              <button
                                                onClick={() => removeRow(originalIdx)}
                                                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-0.5 rounded transition-all duration-200"
                                                title="Delete Stacked Row"
                                              >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                                                </svg>
                                              </button>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </Fragment>
                              );
                            }
                            
                            return (
                              <tr 
                                {...provided.draggableProps} 
                                ref={provided.innerRef} 
                                className={`group relative border-b border-gray-300 hover:shadow-md transition-all duration-200 bg-white hover:bg-gray-50 ${snapshot.isDragging ? 'shadow-lg bg-white border-blue-400' : ''} ${
                                  pendingSectionStart && selectionRange.preview.includes(originalIdx) ? 'bg-blue-100 border-blue-400' : ''
                                } ${
                                  selectedRowsForTotal.includes(originalIdx) ? 'bg-green-100 border-green-400' : ''
                                }`}
                                data-row-index={originalIdx}
                                onMouseEnter={() => handleRowHover(originalIdx)}
                                onClick={() => completeRangeSelection(originalIdx)}
                              >
                                <td {...provided.dragHandleProps} className={`cursor-grab text-center border-r border-gray-300 text-gray-500 hover:text-gray-700 py-1 relative w-6 min-w-6 max-w-6`}>
                                  {pendingSectionStart ? (
                                    <div className={`absolute inset-0 flex items-center justify-center rounded-sm mx-1 my-0.5 transition-colors ${
                                      selectionRange.preview.includes(originalIdx) ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'
                                    }`}>
                                      {selectedRowsForTotal.includes(originalIdx) ? '✓' : (selectionRange.preview.includes(originalIdx) ? '◯' : '⠿')}
                                    </div>
                                  ) : (
                                    '⠿'
                                  )}
                                </td>
                                <td className={`text-center border-r border-gray-300 font-semibold text-gray-700 py-1 w-6 min-w-6 max-w-6`}>{rowNumber}</td>
                                
                                {/* Item Cell */}
                                <td className={`border-r border-gray-300 font-semibold text-sm cursor-pointer hover:bg-gray-50 transition-all duration-150 border-b border-gray-200 py-1 px-1 text-blue-800 ${getItemWidthClass()}`}>
                                  {/* For vitals module, this cell will be merged with objective */}
                                  {isCustomModule(row) && row.moduleType === 'vital-signs' ? null : (
                                    <EditableCell
                                      value={row.item}
                                      onChange={(value) => handleRowChange(originalIdx, 'item', value)}
                                      onConfirm={handleCellConfirm}
                                      onCancel={handleCellCancel}
                                      isHeader={false}
                                      rowIndex={originalIdx}
                                      field="item"
                                      editingCell={editingCell}
                                      setEditingCell={setEditingCell}
                                      tempCellValue={tempCellValue}
                                      setTempCellValue={setTempCellValue}
                                    />
                                  )}
                                </td>
                                
                                {/* Objective Cell - spans across both objective columns */}
                                <td 
                                  className={`border-r border-gray-300 text-sm cursor-pointer hover:bg-gray-50 transition-all duration-150 py-0 px-1 ${getObjectiveWidthClass()}`}
                                  colSpan={2}
                                  {...(isCustomModule(row) && row.moduleType === 'vital-signs' ? { colSpan: 3, className: `border-r border-gray-300 text-sm cursor-pointer hover:bg-gray-50 transition-all duration-150 py-0 px-1 ${getItemWidthClass()} ${getObjectiveWidthClass()}` } : {})}
                                >
                                  {isCustomModule(row) && row.moduleType !== 'stacked-row' ? (
                                    <div className="text-sm">
                                      {renderCustomModule(row, (field, value) => handleRowChange(originalIdx, field, value), false, {
                                        editingCell,
                                        setEditingCell,
                                        tempCellValue,
                                        setTempCellValue,
                                        onConfirm: handleCellConfirm,
                                        onCancel: handleCellCancel,
                                        rowIndex: originalIdx
                                      })}
                                    </div>
                                  ) : (
                                    <EditableCell
                                      value={row.objective}
                                      onChange={(value) => handleRowChange(originalIdx, 'objective', value)}
                                      onConfirm={handleCellConfirm}
                                      onCancel={handleCellCancel}
                                      isHeader={false}
                                      rowIndex={originalIdx}
                                      field="objective"
                                      editingCell={editingCell}
                                      setEditingCell={setEditingCell}
                                      tempCellValue={tempCellValue}
                                      setTempCellValue={setTempCellValue}
                                    />
                                  )}
                                </td>
                                
                                {/* Actions Cell */}
                                <td className="text-center py-0 sticky right-0 bg-white border-l-2 border-gray-400 shadow-lg z-10 w-16 min-w-16 max-w-16">
                                  <div className="flex items-center justify-center gap-0 flex-col">
                                    <button
                                      onClick={() => convertToBlankRow(originalIdx)}
                                      className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 p-0.5 rounded transition-all duration-200"
                                      title="Convert to Blank Row"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() => removeRow(originalIdx)}
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50 p-0.5 rounded transition-all duration-200"
                                      title="Delete Row"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                                      </svg>
                                    </button>
                                  </div>
                                </td>

                              </tr>
                            );
                          }}
                        </Draggable>
                          );
                        })}
                      {provided.placeholder}
                    </tbody>
                  )}
                </Droppable>
              </table>
            </div>
          </div>

          {message && (
            <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded animate-in fade-in duration-300">
              {message}
            </div>
          )}
        </div>

        {/* Custom Module Modal */}
        <CustomModuleModal
          isOpen={showCustomModuleModal}
          onClose={() => setShowCustomModuleModal(false)}
          onSelectModule={handleCustomModuleSelect}
        />
        </div>
      </div>
      </DragDropContext>
    </>
  );
};

export default ScenarioEditor;
